import React, { useEffect, useState } from 'react';
import { Users, Plus, LogIn, Trash2, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';
import AddLinkedAccountModal from './AddLinkedAccountModal';
import { useLinkedT } from '../utils/linkedAccountsTranslations';

/**
 * Displays the current user's linked accounts (max 1) and lets them:
 *  - add another account of the opposite kind (PRO↔ID)
 *  - switch to it (issues a new JWT, replaces token in axios/localStorage, reloads)
 *  - unlink it
 */
const LinkedAccountsPanel = () => {
  const { user, refreshUser } = useAuth();
  const lt = useLinkedT();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get('/api/accounts/linked');
      setAccounts(res.data.accounts || []);
    } catch (e) {
      // 401 / non-auth → nothing to show
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSwitch = async (acc) => {
    setSwitching(true);
    try {
      const res = await axios.post(`/api/accounts/switch/${acc.id}`);
      const token = res.data.token;
      if (!token) throw new Error('missing token');
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      await refreshUser?.();
      toast.success(lt('switched', { name: acc.name || acc.username || 'utilisateur' }));
      // Full reload so every hook (AuthContext, sockets, queries) picks up the new identity
      setTimeout(() => { window.location.href = '/'; }, 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || lt('switchError'));
      setSwitching(false);
    }
  };

  const handleUnlink = async (acc) => {
    if (!window.confirm(lt('removeConfirm', { name: acc.name || acc.username }))) return;
    try {
      const res = await axios.delete(`/api/accounts/linked/${acc.id}`);
      setAccounts(res.data.accounts || []);
      toast.success(lt('removed'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const currentType = user?.account_type || 'ID';
  const canAdd = accounts.length === 0;
  const needKind = currentType === 'PRO' ? 'ID' : 'PRO';

  return (
    <>
      <div className="flex items-center space-x-3 mb-4">
        <Users className="w-6 h-6 text-cyan-400" />
        <h2 className="text-xl font-semibold">{lt('title')}</h2>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 text-sm py-2"><Loader2 className="w-4 h-4 animate-spin" /> {lt('loading')}</div>
      ) : (
        <>
          {accounts.length === 0 && (
            <p className="text-sm text-gray-400 mb-4">
              {lt('empty', { kind: needKind })}
            </p>
          )}

          {accounts.map((acc) => (
            <div key={acc.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10 mb-2" data-testid={`linked-account-row-${acc.id}`}>
              <button
                type="button"
                onClick={() => handleSwitch(acc)}
                disabled={switching}
                className="flex-1 flex items-center gap-3 text-left disabled:opacity-50"
                data-testid={`switch-account-${acc.id}`}
              >
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-magenta-500 to-violet-600 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {acc.profile_image_url ? (
                    <img src={getMediaUrl(acc.profile_image_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-bold">{(acc.name || acc.username || '?').charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold truncate">{acc.name || acc.username}</p>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${acc.account_type === 'PRO' ? 'bg-amber-500/20 text-amber-300' : 'bg-cyan-500/20 text-cyan-300'}`}>
                      {acc.account_type}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">@{acc.username} · {acc.email_masked}</p>
                </div>
                <LogIn className="w-4 h-4 text-gray-400" />
              </button>
              <button
                type="button"
                onClick={() => handleUnlink(acc)}
                className="p-2 rounded-full hover:bg-red-500/10 text-red-400"
                title="Retirer"
                data-testid={`unlink-account-${acc.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          {canAdd && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="w-full mt-2 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-dashed border-white/20 text-sm font-medium"
              data-testid="open-add-linked-account"
            >
              <Plus className="w-4 h-4" /> {lt('addBtn', { kind: needKind })}
            </button>
          )}

          <div className="mt-3 text-[11px] text-gray-500 flex items-start gap-2">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{lt('footNote')}</span>
          </div>
        </>
      )}

      {showAdd && (
        <AddLinkedAccountModal
          currentAccountType={currentType}
          onClose={() => setShowAdd(false)}
          onLinked={(next) => {
            setAccounts(next);
            setShowAdd(false);
          }}
        />
      )}
    </>
  );
};

export default LinkedAccountsPanel;
